import React, { useEffect, useState } from 'react';
import { 
  Tabs, 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  message, 
  Popconfirm,
  Card,
  Space,
  Tag,
  Select
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  HeartOutlined,
  CompassOutlined,
  TeamOutlined,
  AppstoreOutlined,
  IdcardOutlined,
  FlagOutlined
} from '@ant-design/icons';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { fetchCategories, saveCategories } from '../../services/firestoreService';
import IdentityPreferencesTab from './IdentityPreferencesTab';
import ManageStatusTab from './ManageStatusTab';

const normalizeFieldOptions = (items = []) =>
  items.map((item) => {
    if (item.label && Array.isArray(item.options)) {
      return item;
    }
    return {
      id: item.id,
      label: item.label || item.name || '',
      status: item.status || 'active',
      options: item.options || [],
    };
  });

const CategoryManagement = () => {
  const { logActivity } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('interests');
  const [categories, setCategories] = useState({
    interests: [],
    positions: [],
    relationships: [],
    tabs: [],
    identityPreferences: [],
    manageStatus: []
  });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [form] = Form.useForm();
  const [loadingCategories, setLoadingCategories] = useState(false);

  useEffect(() => {
    loadCategories();
    logActivity('view_categories', 'Viewed category management page');
  }, []);

  const loadCategories = async () => {
    setLoadingCategories(true);
    try {
      const data = await fetchCategories();
      setCategories({
        interests: data.interests || [],
        positions: data.positions || [],
        relationships: data.relationships || [],
        tabs: data.tabs || [],
        identityPreferences: normalizeFieldOptions(data.identityPreferences || []),
        manageStatus: normalizeFieldOptions(data.manageStatus || []),
      });
    } catch {
      message.error('Failed to load categories from Firebase');
    } finally {
      setLoadingCategories(false);
    }
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditCategory = (category) => {
    setEditingCategory(category);
    form.setFieldsValue(category);
    setIsModalVisible(true);
  };

  const handleSaveCategory = () => {
    return form.validateFields().then(async (values) => {
      let updatedCategories = { ...categories };

      if (editingCategory) {
        updatedCategories[activeTab] = categories[activeTab].map((cat) =>
          cat.id === editingCategory.id ? { ...cat, ...values } : cat
        );
        message.success('Category updated successfully');
        logActivity('update_category', `Updated ${activeTab} category: ${values.name}`);
      } else {
        const maxId = categories[activeTab].length
          ? Math.max(...categories[activeTab].map((c) => c.id))
          : 0;
        const newCategory = {
          id: maxId + 1,
          ...values,
        };
        updatedCategories[activeTab] = [...categories[activeTab], newCategory];
        message.success('Category added successfully');
        logActivity('create_category', `Created ${activeTab} category: ${values.name}`);
      }

      try {
        await saveCategories(updatedCategories);
        setCategories(updatedCategories);
        setIsModalVisible(false);
      } catch {
        message.error('Failed to save category to Firebase');
        return Promise.reject();
      }
    });
  };

  const saveIdentityPreferences = async (updatedFields) => {
    const updatedCategories = { ...categories, identityPreferences: updatedFields };
    await saveCategories(updatedCategories);
    setCategories(updatedCategories);
  };

  const saveManageStatus = async (updatedFields) => {
    const updatedCategories = { ...categories, manageStatus: updatedFields };
    await saveCategories(updatedCategories);
    setCategories(updatedCategories);
  };

  const handleDeleteCategory = async (category) => {
    const updatedCategories = { ...categories };
    updatedCategories[activeTab] = categories[activeTab].filter((cat) => cat.id !== category.id);

    try {
      await saveCategories(updatedCategories);
      setCategories(updatedCategories);
      message.error(`Category "${category.name}" deleted`);
      logActivity('delete_category', `Deleted ${activeTab} category: ${category.name}`);
    } catch {
      message.error('Failed to delete category');
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: 'Icon',
      dataIndex: 'icon',
      key: 'icon',
      width: 100,
      render: (icon) => <span className="text-2xl">{icon}</span>
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <span className="font-semibold">{name}</span>
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'red'}>
          {status?.toUpperCase()}
        </Tag>
      )
    },
    ...(activeTab === 'tabs' ? [{
      title: 'Route',
      dataIndex: 'route',
      key: 'route',
      render: (route) => <code className="bg-gray-100 px-2 py-1 rounded text-sm">{route}</code>
    }] : []),
    {
      title: 'Actions',
      key: 'actions',
      render: (_, category) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditCategory(category)}
            className="text-blue-500"
          />
          <Popconfirm
            title="Are you sure you want to delete this category?"
            onConfirm={() => handleDeleteCategory(category)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="link"
              icon={<DeleteOutlined />}
              danger
            />
          </Popconfirm>
        </Space>
      )
    }
  ];

  const tabItems = [
    {
      key: 'interests',
      label: (
        <span className="flex items-center gap-2">
          <HeartOutlined />
          Interests
        </span>
      ),
      children: (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold">Interest Categories</h3>
              <p className="text-gray-500 text-sm">Manage user interest options</p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCategory}
              className="bg-cyan-400 hover:bg-cyan-500"
            >
              Add Interest
            </Button>
          </div>
          <Table
            columns={columns}
            dataSource={categories.interests}
            rowKey="id"
            pagination={false}
          />
        </Card>
      )
    },
    {
      key: 'positions',
      label: (
        <span className="flex items-center gap-2">
          <CompassOutlined />
          Positions
        </span>
      ),
      children: (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold">Position Categories</h3>
              <p className="text-gray-500 text-sm">Manage position preference options</p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCategory}
              className="bg-cyan-400 hover:bg-cyan-500"
            >
              Add Position
            </Button>
          </div>
          <Table
            columns={columns}
            dataSource={categories.positions}
            rowKey="id"
            pagination={false}
          />
        </Card>
      )
    },
    {
      key: 'relationships',
      label: (
        <span className="flex items-center gap-2">
          <TeamOutlined />
          Relationships
        </span>
      ),
      children: (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold">Relationship Types</h3>
              <p className="text-gray-500 text-sm">Manage relationship preference options</p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCategory}
              className="bg-cyan-400 hover:bg-cyan-500"
            >
              Add Relationship Type
            </Button>
          </div>
          <Table
            columns={columns}
            dataSource={categories.relationships}
            rowKey="id"
            pagination={false}
          />
        </Card>
      )
    },
    {
      key: 'tabs',
      label: (
        <span className="flex items-center gap-2">
          <AppstoreOutlined />
          App Tabs
        </span>
      ),
      children: (
        <Card>
          <div className="flex justify-between items-center mb-4">
            <div>
              <h3 className="text-lg font-semibold">Navigation Tabs</h3>
              <p className="text-gray-500 text-sm">Manage app navigation tabs</p>
            </div>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddCategory}
              className="bg-cyan-400 hover:bg-cyan-500"
            >
              Add Tab
            </Button>
          </div>
          <Table
            columns={columns}
            dataSource={categories.tabs}
            rowKey="id"
            pagination={false}
          />
        </Card>
      )
    },
    {
      key: 'identityPreferences',
      label: (
        <span className="flex items-center gap-2">
          <IdcardOutlined />
          Identity Preferences
        </span>
      ),
      children: (
        <Card>
          <IdentityPreferencesTab
            fields={categories.identityPreferences}
            onSave={saveIdentityPreferences}
            logActivity={logActivity}
          />
        </Card>
      )
    },
    {
      key: 'manageStatus',
      label: (
        <span className="flex items-center gap-2">
          <FlagOutlined />
          Manage Status
        </span>
      ),
      children: (
        <Card>
          <ManageStatusTab
            fields={categories.manageStatus}
            onSave={saveManageStatus}
            logActivity={logActivity}
          />
        </Card>
      )
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-800">Category Management</h1>
        <p className="text-gray-500 mt-1">Manage app categories and options</p>
      </div>

      {/* Tabs */}
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        size="large"
      />

      {/* Add/Edit Modal */}
      <Modal
        title={editingCategory ? 'Edit Category' : 'Add New Category'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onOk={handleSaveCategory}
        okText={editingCategory ? 'Update' : 'Create'}
        width={500}
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            label="Name"
            name="name"
            rules={[{ required: true, message: 'Name is required' }]}
          >
            <Input placeholder="Enter category name" />
          </Form.Item>

          <Form.Item
            label="Icon (Emoji)"
            name="icon"
            rules={[{ required: true, message: 'Icon is required' }]}
          >
            <Input placeholder="Enter emoji (e.g., ⚽)" maxLength={2} />
          </Form.Item>

          {activeTab === 'tabs' && (
            <Form.Item
              label="Route"
              name="route"
              rules={[{ required: true, message: 'Route is required' }]}
            >
              <Input placeholder="/example" />
            </Form.Item>
          )}

          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Please select a status' }]}
          >
            <Select placeholder="Select status" allowClear>
              <Select.Option value="active">Active</Select.Option>
              <Select.Option value="inactive">Inactive</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default CategoryManagement;
